import { useState, useEffect, useContext } from "react";
import type { FormEvent } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { SectionLabel } from "../../components/ui";
import { useAuth } from "../../auth/auth-context";
import { AppStoreContext } from "../../state/app-store-context";
import { createDefaultHkdseDeck } from "../../lib/deck";

type Mode = "login" | "signup";

const inputClass =
  "w-full box-border px-[16px] py-[15px] rounded-[13px] bg-white border border-line " +
  "text-[15px] text-ink placeholder:text-ink4/70 outline-none focus:border-ink";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, signUp } = useAuth();
  const store = useContext(AppStoreContext);

  // 解析 next query parameter，驗證為同源路徑
  const searchParams = new URLSearchParams(location.search);
  const rawNext = searchParams.get("next");
  const nextPath = rawNext && rawNext.startsWith("/") && !rawNext.startsWith("//")
    ? decodeURIComponent(rawNext)
    : "/today";

  const isTestMode = searchParams.get("test_mode") === "1" || searchParams.get("test") === "1";

  const ensureStarterDeck = () => {
    if (store) {
      if (!store.state.decks || store.state.decks.length === 0) {
        store.dispatch({ type: "addDeck", deck: createDefaultHkdseDeck() });
      }
      store.dispatch({ type: "setOnboarded", value: true });
    }
  };

  const handleGuestTestEnter = () => {
    ensureStarterDeck();
    navigate(nextPath, { replace: true });
  };

  useEffect(() => {
    // 驗證 next 必須是安全的內部路徑
    try {
      if (rawNext) {
        const decoded = decodeURIComponent(rawNext);
        if (!decoded.startsWith("/") || decoded.startsWith("//")) {
          console.warn("Invalid next path, falling back to /today");
        }
      }
    } catch {
      console.warn("Failed to decode next path");
    }
  }, [rawNext]);
  const [mode, setMode] = useState<Mode>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);

  const switchMode = (next: Mode) => {
    setMode(next);
    setError("");
    setInfo("");
    setConfirmPassword("");
  };

  const submit = async () => {
    setError("");
    setInfo("");
    if (!email.trim()) {
      setError("請輸入電郵地址");
      return;
    }
    if (password.length < 6) {
      setError("密碼至少需要 6 個字元");
      return;
    }
    if (mode === "signup" && password !== confirmPassword) {
      setError("兩次輸入的密碼不一致");
      return;
    }

    setBusy(true);
    try {
      if (mode === "login") {
        const result = await signIn(email.trim(), password);
        if (result.error) setError(result.error);
        else {
          ensureStarterDeck();
          navigate(nextPath, { replace: true });
        }
      } else {
        const result = await signUp(email.trim(), password);
        if (result.error) setError(result.error);
        else if (result.needsConfirm) setInfo("驗證郵件已發送，請先查收再登入");
        else {
          ensureStarterDeck();
          navigate(nextPath, { replace: true });
        }
      }
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    void submit();
  };

  const isLogin = mode === "login";

  return (
    <div className="min-h-dvh box-border bg-cream flex flex-col px-[max(26px,5vw)] pt-[60px] pb-[34px]">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="self-start font-grotesk text-[13.5px] text-ink4 cursor-pointer bg-transparent border-none p-0"
      >
        ← 返回
      </button>

      <div className="mt-[44px]">
        <div className="flex items-center justify-between">
          <SectionLabel>MANGO WORD · 帳戶</SectionLabel>
          {isTestMode ? (
            <span className="font-grotesk text-[11px] font-bold px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-700 dark:text-amber-300 border border-amber-500/30">
              🧪 測試模式中 (免測驗)
            </span>
          ) : (
            <button
              type="button"
              onClick={() => {
                const sp = new URLSearchParams(location.search);
                sp.set("test_mode", "1");
                navigate(`${location.pathname}?${sp.toString()}`, { replace: true });
              }}
              className="text-[12px] font-grotesk text-ink4 hover:text-amber-600 bg-transparent border-none cursor-pointer p-0"
              title="啟用測試模式"
            >
              切換至測試模式 🧪
            </button>
          )}
        </div>
        <h1 className="mt-[14px] mb-0 text-[32px] leading-[1.25] font-bold text-ink tracking-[-.01em]">
          {isLogin ? "登入 Mango Word" : "建立 Mango Word 帳戶"}
        </h1>
        <p className="mt-[12px] mb-0 text-[15px] leading-[1.7] text-ink4">
          {isLogin ? "登入後即可同步你嘅詞單同學習進度。" : "建立帳戶，喺任何裝置繼續你嘅背詞進度。"}
        </p>

        {isTestMode && (
          <div className="mt-4 p-3.5 rounded-xl border border-dashed border-amber-500/40 bg-amber-500/10 text-amber-800 dark:text-amber-200 flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
            <div className="text-[13px] leading-snug">
              <div className="font-bold flex items-center gap-1.5">
                <span>🧪</span>
                <span>測試模式：已為你免除入學水平測驗</span>
              </div>
              <div className="text-[12px] opacity-80 mt-0.5">
                登入或註冊後自動建立 HKDSE 3,000 詞題庫並直達學習排程。
              </div>
            </div>
            <button
              type="button"
              onClick={handleGuestTestEnter}
              className="self-start sm:self-center font-grotesk text-[12px] font-bold px-3 py-1.5 rounded-lg bg-amber-600 dark:bg-amber-500 text-white hover:opacity-90 active:scale-95 transition-all cursor-pointer whitespace-nowrap shadow-xs"
            >
              免註冊直接體驗 ›
            </button>
          </div>
        )}
      </div>

      <div className="mt-[28px] grid grid-cols-2 gap-1 rounded-[12px] bg-paper p-1" role="tablist" aria-label="帳戶模式">
        {(["login", "signup"] as const).map((tabMode) => (
          <button
            key={tabMode}
            type="button"
            role="tab"
            aria-selected={mode === tabMode}
            onClick={() => switchMode(tabMode)}
            className={`rounded-[9px] py-[10px] text-[14px] font-bold cursor-pointer ${
              mode === tabMode ? "bg-white text-ink shadow-sm" : "bg-transparent text-ink4"
            }`}
          >
            {tabMode === "login" ? "登入" : "註冊"}
          </button>
        ))}
      </div>

      <form onSubmit={handleSubmit} className="mt-[22px] flex flex-col gap-[13px]" noValidate>
        <label className="flex flex-col gap-2 text-[12px] font-medium text-ink3">
          電郵地址
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="email"
            aria-label="電郵地址"
            className={inputClass}
          />
        </label>
        <label className="flex flex-col gap-2 text-[12px] font-medium text-ink3">
          密碼
          <input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete={isLogin ? "current-password" : "new-password"}
            aria-label="密碼"
            className={inputClass}
          />
        </label>
        {!isLogin && (
          <label className="flex flex-col gap-2 text-[12px] font-medium text-ink3">
            確認密碼
            <input
              type="password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              autoComplete="new-password"
              aria-label="確認密碼"
              className={inputClass}
            />
          </label>
        )}

        {error && <div role="alert" className="text-[13.5px] text-accent-deep">{error}</div>}
        {info && <div role="status" className="text-[13.5px] text-ink">{info}</div>}

        <button
          type="submit"
          disabled={busy}
          aria-busy={busy}
          className="mt-[6px] w-full rounded-full py-4 text-[16px] font-bold cursor-pointer bg-ink text-cream disabled:opacity-50"
        >
          {busy ? "處理中…" : isLogin ? "登入" : "建立帳戶"}
        </button>
      </form>

      <div className="mt-auto pt-[26px] text-center">
        {isLogin && (
          <button
            type="button"
            onClick={() => navigate("/account/reset-password")}
            className="font-grotesk text-[13.5px] text-accent cursor-pointer bg-transparent border-none p-0"
          >
            忘記密碼？
          </button>
        )}
        <div className="h-[18px]" />
        <button
          type="button"
          onClick={() => switchMode(isLogin ? "signup" : "login")}
          className="font-grotesk text-[13.5px] text-ink4 cursor-pointer bg-transparent border-none p-0"
        >
          {isLogin ? "未有帳戶？註冊" : "已有帳戶？登入"}
        </button>
      </div>
    </div>
  );
}
